import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import constants from "../constants/constants";
import {
  Info,
  ThumbsUp,
  ShieldCheck,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import ReviewsModal from "../components/ReviewsModal";
import OverlayedImage from "../components/OverlayedImage";

/* ----------------------------------------
   Constants
---------------------------------------- */
const BASE_PRODUCT_IMAGES = [
  constants.productImage2,
  constants.productImage3,
  constants.productImage5,
];

const NAV_IMAGE = constants.transparentOverlay;

/* ----------------------------------------
   Grade-based copy
---------------------------------------- */
const GRADE_COPY: Record<number, any> = {
  1: {
    headline: (
      <>
        The 7‑Day <span className="text-primary-700">Stop Balding</span> Blueprint
      </>
    ),
    badge: "For men with early signs of hair loss",
    elevator:
      "You're one of the lucky ones. Your hair loss is still in its early stages but there are signs of future hair loss. This plan is personalized to your stage and it's designed to stop hair loss early before it gets out of hand.",
    routine: [
      "Daily actions to stop early thinning",
      "Product guidance for prevention",
      "Avoid mistakes that speed up hair loss",
      "A clear plan to maintain thicker hair",
    ],
    helpTitle: "How this helps early hair loss",
    help: [
      "Targets early hair loss immediately",
      "Prevents progression before damage",
      "Builds habits that protect follicles",
      "Warns of harmful products and practices",
    ],
    primaryCTA: "Take Control Today",
    secondaryCTA: "Start Now",
    reviewNote:
      "Ideal for men noticing the first signs of thinning or hairline recession.",
  },
  2: {
    headline: (
      <>
        The 7‑Day{" "}
        <span className="text-primary-700">Hair Loss Control</span> Protocol
      </>
    ),
    badge: "For men with noticeable hair loss already",
    elevator:
      "Thinning is already visible, but you're still at a reversible stage. This plan shows you how to fight back using proven methods.",
    routine: [
      "Daily steps to trigger regrowth",
      "How to combine treatments",
      "What works vs what harms hair",
      "A clear plan to stay consistent",
    ],
    helpTitle: "How this helps late‑stage hair loss",
    help: [
      "Focuses on regrowth and thickening",
      "Explains medical & topical options",
      "Shows safe combinations that work",
      "Avoids fake cures",
    ],
    primaryCTA: "Take Control Today",
    secondaryCTA: "Start Now",
    reviewNote:
      "Designed for men who are starting to notice thinning or shedding.",
  },
  3: {
    headline: (
      <>
        The 7‑Day{" "}
        <span className="text-primary-700">Rapid Regrowth</span> Routine
      </>
    ),
    badge: "Put an end to your hair loss anxiety",
    elevator:
      "Even with advanced hair loss, results are still possible. This plan focuses on the highest‑impact regrowth strategies.",
    routine: [
      "Aggressive evidence‑based routine",
      "Advanced options explained clearly",
      "Strategies for all budgets",
      "No fluff — only what works",
    ],
    helpTitle: "How this helps advanced hair loss",
    help: [
      "Focuses on maximum regrowth potential",
      "Covers advanced options",
      "Shows realistic outcomes",
      "Gives clarity and control",
    ],
    primaryCTA: "Start Regrowth Now",
    secondaryCTA: "Control Your Anxiety",
    reviewNote:
      "Designed for men with advanced or long‑term hair loss.",
  },
};

/* ----------------------------------------
   Helpers
---------------------------------------- */
function getGrade(param: string | null): number {
  if (param === "2") return 2;
  if (param === "3") return 3;
  return 1;
}

/* ----------------------------------------
   Component
---------------------------------------- */
const ProductPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const grade = getGrade(searchParams.get("grade"));
  const copy = GRADE_COPY[grade];

  const injectedImage = searchParams.get("imageUrl");
  const hasUserImage = Boolean(injectedImage);

  const productImages = hasUserImage
    ? [injectedImage as string, ...BASE_PRODUCT_IMAGES]
    : [constants.productImage1, ...BASE_PRODUCT_IMAGES];

  const [activeImg, setActiveImg] = useState(0);
  const [showReviews, setShowReviews] = useState(false);

  const prevImg = () =>
    setActiveImg((i) => (i === 0 ? productImages.length - 1 : i - 1));
  const nextImg = () =>
    setActiveImg((i) => (i + 1) % productImages.length);

  const CTA = ({
    label,
    className = "",
  }: {
    label: string;
    className?: string;
  }) => (
    <button
      onClick={() =>
        navigate(
          `/checkout?grade=${grade}&imageUrl=${encodeURIComponent(
            productImages[activeImg]
          )}`
        )
      }
      className={`w-full sm:w-auto px-7 py-3 rounded-xl font-bold shadow-md text-white bg-primary-600 hover:bg-green-700 text-lg transition ${className}`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-blue-50 flex flex-col">
      <ReviewsModal open={showReviews} onClose={() => setShowReviews(false)} />

      {/* Hero */}
      <div className="flex flex-col md:flex-row gap-8 px-6 py-10 bg-white shadow-md rounded-b-3xl">
        {/* Image Carousel */}
        <div className="flex-1 flex justify-center">
          <div className="relative w-full max-w-sm aspect-square bg-gray-100 rounded-2xl overflow-hidden flex items-center justify-center">
            <button
              onClick={prevImg}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow border z-20"
            >
              <ChevronLeft className="w-6 h-6 text-gray-500" />
            </button>

            <div className="relative w-full h-full">
              {activeImg === 0 && hasUserImage ? (
                <OverlayedImage
                  baseImageUrl={injectedImage as string}
                  overlayImageUrl={NAV_IMAGE}
                  size={400}
                  className="w-full h-full object-cover rounded-2xl"
                  alt="User image with overlay"
                />
              ) : (
                <img
                  src={productImages[activeImg]}
                  className="w-full h-full object-cover rounded-2xl"
                  alt="Product"
                  draggable={false}
                />
              )}
            </div>

            <button
              onClick={nextImg}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow border z-20"
            >
              <ChevronRight className="w-6 h-6 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Copy */}
        <div className="flex-1 flex flex-col gap-4">
          <h1 className="text-3xl font-bold text-gray-900">{copy.headline}</h1>

          <span className="bg-green-50 text-green-800 px-3 py-1 rounded-full text-sm font-semibold border w-fit">
            {copy.badge}
          </span>

          <div className="flex items-center gap-4">
            <span className="line-through text-red-500">$22</span>
            <span className="text-2xl font-bold">$8</span>
            <span
              className="text-yellow-500 cursor-pointer"
              onClick={() => setShowReviews(true)}
            >
              4.6 ★ (142)
            </span>
          </div>

          <CTA label={copy.primaryCTA} />

          <div className="flex items-center gap-2 text-sm font-semibold">
            <ThumbsUp className="w-4 h-4 text-green-500" />
            3,462 men purchased this guide
          </div>

          <div className="flex items-center gap-2 text-sm font-semibold">
            <ShieldCheck className="w-4 h-4 text-green-500" />
            30-day money-back guarantee
          </div>

          <p className="text-gray-700">{copy.elevator}</p>
        </div>
      </div>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-4 py-10 flex flex-col gap-10">
        <section className="bg-white shadow-xl rounded-2xl p-6 text-center">
          <Info className="mx-auto mb-2 text-primary-700" />
          <p className="text-gray-700">
            Personalized plan based on your detected stage of hair loss.
          </p>
        </section>
      </main>
    </div>
  );
};

export default ProductPage;
